import React, { useEffect, useRef } from "react";

interface ChromaKeyVideoProps {
  src: string;
  keyColor?: [number, number, number];
  renderWidth?: number;
  renderHeight?: number;
  className?: string;
  style?: React.CSSProperties;
}

const ChromaKeyVideo: React.FC<ChromaKeyVideoProps> = ({
  src,
  keyColor = [139, 226, 120],
  renderWidth = 640,
  renderHeight = 360,
  className,
  style,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    video.play().catch(() => undefined);

    const [kr, kg, kb] = keyColor;
    const innerThreshold = 42;
    const outerThreshold = 95;
    let rafId = 0;
    let cancelled = false;

    const draw = () => {
      if (cancelled) return;
      if (video.readyState >= 2) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = frame.data;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const dr = r - kr;
          const dg = g - kg;
          const db = b - kb;
          const dist = Math.sqrt(dr * dr + dg * dg + db * db);
          if (dist < innerThreshold) {
            data[i + 3] = 0;
          } else if (dist < outerThreshold) {
            const t =
              (dist - innerThreshold) / (outerThreshold - innerThreshold);
            data[i + 3] = Math.round(255 * t);
            data[i + 1] = Math.round(g * t + ((r + b) / 2) * (1 - t));
          }
        }
        ctx.putImageData(frame, 0, 0);
      }
      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [keyColor]);

  return (
    <div className={className} style={{ position: "relative", ...style }}>
      <video
        ref={videoRef}
        src={src}
        autoPlay
        loop
        muted
        playsInline
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
      />
      <canvas
        ref={canvasRef}
        width={renderWidth}
        height={renderHeight}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
};

export default ChromaKeyVideo;
